import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PortalShellPageHeader from "../../components/Layout/PortalShellPageHeader/PortalShellPageHeader";
import portalStyles from "../../layout/portalPageShell.module.css";
import styles from "./ProductsPage.module.css";
import UserProductCard from "../../components/Layout/UserProductCard/UserProductCard";
import { useAuth } from "../../context/authContext";
import { getUserSubscriptionPlans } from "../../services/subscription-plan-service";
import { getUserDomainPlans } from "../../services/domain-plan-service";
import { safeFormatDate } from "../../utils/dateUtils.js";

export default function ProductsPage() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("maintenance");
  const [products, setProducts] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [domainsError, setDomainsError] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getUserSubscriptionPlans();

      if (response.success && Array.isArray(response.data)) {
        const mappedProducts = response.data.map((plan) => {
          const statusNorm = String(plan.status || "").toLowerCase();
          const displayStatus =
            statusNorm === "active" || statusNorm === "paid"
              ? "online"
              : "offline";

          return {
            id: plan._id || plan.id,
            projectImage: plan.projectImage || "",
            projectName: plan.product || "Untitled Product",
            projectUrl: plan.projectUrl || "",
            expirationDate: plan.renewalDate
              ? new Date(plan.renewalDate).toISOString().split("T")[0]
              : null,
            status: displayStatus,
            releaseDate: plan.createdAt
              ? new Date(plan.createdAt).toISOString().split("T")[0]
              : new Date().toISOString().split("T")[0],
            currentPlan: plan.subscriptionPlan || plan.projectTier || "Basic",
            websiteType: plan.websiteType || "Business Website",
            projectId: plan.projectId,
            statusOriginal: plan.status,
          };
        });

        setProducts(mappedProducts);
      } else {
        setProducts([]);
      }
    } catch (err) {
      setError(err.message || "Failed to load products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDomains = useCallback(async () => {
    try {
      setDomainsLoading(true);
      setDomainsError(null);

      const response = await getUserDomainPlans();

      if (response.success && Array.isArray(response.data)) {
        setDomains(response.data);
      } else {
        setDomains([]);
      }
    } catch (err) {
      setDomainsError(err.message || "Failed to load domains");
      setDomains([]);
    } finally {
      setDomainsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts, user]);

  useEffect(() => {
    const nextTab = location?.state?.tab;
    if (nextTab === "maintenance" || nextTab === "domains") {
      setActiveTab(nextTab);
    }
  }, [location.key, location?.state?.tab]);

  useEffect(() => {
    if (activeTab === "domains") {
      fetchDomains();
    }
  }, [activeTab, fetchDomains]);

  const formatDate = (dateString) => {
    if (!dateString || Number.isNaN(new Date(dateString).getTime())) {
      return t("notAvailable");
    }
    return safeFormatDate(dateString, {}, "Invalid Date");
  };

  const getStatusBadge = (renewalDate) => {
    if (!renewalDate) {
      return {
        text: t("productsPage.status.unknown"),
        className: `${styles.statusBadge} ${styles.statusDefault}`,
      };
    }
    const now = new Date();
    const expDate = new Date(renewalDate);
    const daysUntilExpiry = Math.floor((expDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return {
        text: t("productsPage.status.expired"),
        className: `${styles.statusBadge} ${styles.statusExpired}`,
      };
    }
    if (daysUntilExpiry <= 30) {
      return {
        text: t("productsPage.status.expiringSoon"),
        className: `${styles.statusBadge} ${styles.statusExpiringSoon}`,
      };
    }
    return {
      text: t("productsPage.status.active"),
      className: `${styles.statusBadge} ${styles.statusActive}`,
    };
  };

  return (
    <div className={portalStyles.portalPage}>
      <PortalShellPageHeader
        variant="intro"
        title={t("productsPage.title")}
        subtitle={t("productsPage.subtitle")}
      />
      <div className={portalStyles.container}>
        <div className={styles.productsMain}>
          <div className={styles.tabNavigation}>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "maintenance" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("maintenance")}
            >
              {t("productsPage.tabs.maintenance")}
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "domains" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("domains")}
            >
              {t("productsPage.tabs.domains")}
            </button>
          </div>

          <div className={styles.tabContent}>
            {activeTab === "maintenance" ? (
              <>
                {loading ? (
                  <div className={styles.loading}>
                    <p>{t("productsPage.loading")}</p>
                  </div>
                ) : null}

                {error ? (
                  <div className={styles.error}>
                    <p>
                      {t("productsPage.error")}: {error}
                    </p>
                  </div>
                ) : null}

                {!loading && !error && products.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon} aria-hidden="true">
                      <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M9 6h10M9 10h10M9 14h7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M7 4h-1a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M8 4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2H8V4z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <p className={styles.emptyStateMessage}>
                      {t("productsPage.noProducts")}
                    </p>
                  </div>
                ) : null}

                {!loading && products.length > 0 ? (
                  <div className={styles.productsGrid}>
                    {products.map((product) => (
                      <UserProductCard key={product.id} product={product} />
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}

            {activeTab === "domains" ? (
              <div className={styles.domainsContent}>
                {domainsLoading ? (
                  <div className={styles.loading}>
                    <p>{t("productsPage.loadingDomains")}</p>
                  </div>
                ) : null}

                {domainsError ? (
                  <div className={styles.error}>
                    <p>
                      {t("productsPage.error")}: {domainsError}
                    </p>
                  </div>
                ) : null}

                {!domainsLoading && !domainsError && domains.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon} aria-hidden="true">
                      <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <path
                          d="M2 12h20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M12 2a14 14 0 0 1 0 20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M12 2a14 14 0 0 0 0 20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <p className={styles.emptyStateMessage}>
                      {t("productsPage.noDomains")}
                    </p>
                  </div>
                ) : null}

                {!domainsLoading && domains.length > 0 ? (
                  <>
                    <div className={styles.tableContainer}>
                      <table className={styles.billingTable}>
                        <thead>
                          <tr>
                            <th>{t("productsPage.tableHeaders.projectId")}</th>
                            <th>{t("productsPage.tableHeaders.domainName")}</th>
                            <th>{t("productsPage.tableHeaders.renewalDate")}</th>
                            <th>{t("productsPage.tableHeaders.status")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {domains.map((domain, index) => {
                            const statusBadge = getStatusBadge(
                              domain.renewalDate
                            );
                            return (
                              <tr
                                key={domain._id || domain.id}
                                className={
                                  index % 2 === 0
                                    ? styles.rowEven
                                    : styles.rowOdd
                                }
                              >
                                <td className={styles.invoiceId}>
                                  {domain.projectId || t("notAvailable")}
                                </td>
                                <td className={styles.domainName}>
                                  <span className={styles.domainIcon}>
                                    {"\u{1F310}"}
                                  </span>
                                  {domain.product}
                                </td>
                                <td>{formatDate(domain.renewalDate)}</td>
                                <td>
                                  <span className={statusBadge.className}>
                                    {statusBadge.text}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className={styles.cardsContainer}>
                      {domains.map((domain) => {
                        const statusBadge = getStatusBadge(domain.renewalDate);
                        return (
                          <div
                            key={domain._id || domain.id}
                            className={styles.domainCard}
                          >
                            <div className={styles.cardHeader}>
                              <div className={styles.domainInfo}>
                                <div className={styles.domainId}>
                                  {domain.projectId || t("notAvailable")}
                                </div>
                                <div className={styles.cardDomainName}>
                                  <span className={styles.domainIcon}>
                                    {"\u{1F310}"}
                                  </span>
                                  {domain.product}
                                </div>
                              </div>
                              <span className={statusBadge.className}>
                                {statusBadge.text}
                              </span>
                            </div>

                            <div className={styles.cardBody}>
                              <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>
                                  {t("productsPage.tableHeaders.renewalDate")}
                                </span>
                                <span className={styles.cardValue}>
                                  {formatDate(domain.renewalDate)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className={styles.footer}>
            <p className={styles.footerText}>{t("productsPage.footer")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
